import React from 'react';
import { Link } from 'react-router-dom';
import { DraftOnly, ProdOnly } from '@components/project/DraftModeRequired.component';
import { enterDraftMode } from '@stores/data.store';
import { PageContentHeader } from '@pages/PageStructure.component';
import SaveButton from '@components/button/SaveButton.component';

export type SemanticLayerActivePage = 'entities' | 'metrics' | 'dimensions' | 'visual';

interface SemanticLayerHeaderProps {
    activePage: SemanticLayerActivePage;
    subtitle?: string;
    search?: string;
    onSearchChange?: (value: string) => void;
    onSave?: () => void;
    saveDisabled?: boolean;
}

const SemanticLayerHeader: React.FC<SemanticLayerHeaderProps> = ({
    activePage,
    subtitle,
    search,
    onSearchChange,
    onSave,
    saveDisabled,
}) => {
    // Default subtitles based on active page
    const defaultSubtitle = activePage === 'visual' 
        ? 'Visually create and connect your entities and metrics.'
        : 'View and manage your business entities and metrics';

    const displaySubtitle = subtitle || defaultSubtitle;

    return (
        <PageContentHeader>
            <div className="d-flex center-vertically">
                <div style={{width: '40%'}}>
                    <h1 className="mb-0">Semantic Layer</h1>
                    <div className="text-muted font-13">
                        {displaySubtitle}
                    </div>
                </div>
                
                {onSearchChange ? (
                    <div className="flex-1 ps-2 pe-2">
                        <input 
                            type="text" 
                            className="form-control input-rounded input-search" 
                            placeholder="Search..." 
                            value={search || ''}
                            onChange={e => onSearchChange(e.target.value)}
                        />
                    </div>
                ) : (
                    <div className="flex-1"></div>
                )}
                
                <div>
                    {onSave && (
                        <DraftOnly>
                            <div className="me-3 d-inline-block">
                                <SaveButton
                                    className=""
                                    disabled={saveDisabled}
                                    onClick={onSave}
                                />
                            </div>
                        </DraftOnly>
                    )}
                    <ProdOnly>
                        <button 
                            className="btn btn-outline-secondary btn-sm hover-only me-2" 
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                enterDraftMode('edit-button');
                            }}
                        >
                            <i className="mdi mdi-pencil"></i> <span className="d-none d-sm-inline">Edit</span>
                        </button>
                    </ProdOnly>
                    <div className="btn-group">
                        <Link 
                            to="/model/list/entities" 
                            className={`btn btn-light ${activePage === 'entities' ? 'active' : ''}`}
                        >
                            <i className="mdi mdi-table-star"></i> Entities
                        </Link>
                        <Link 
                            to="/model/list/metrics" 
                            className={`btn btn-light ${activePage === 'metrics' ? 'active' : ''}`}
                        >
                            <i className="mdi mdi-pound-box"></i> Metrics
                        </Link>
                        <Link 
                            to="/model/list/dimensions" 
                            className={`btn btn-light ${activePage === 'dimensions' ? 'active' : ''}`}
                        >
                            <i className="mdi mdi-axis-arrow"></i> Dimensions
                        </Link>
                        <Link 
                            to="/model/visual" 
                            className={`btn btn-light ${activePage === 'visual' ? 'active' : ''}`}
                        >
                            <i className="mdi mdi-transit-connection-variant"></i> Visual
                        </Link>
                    </div>
                </div>
            </div>
        </PageContentHeader>
    );
};

export default SemanticLayerHeader;

